import { useEffect, useState } from "react";
import { IconShield } from "@veto/ui";
import { wipeAllLocalData } from "@veto/storage";
import { clearWrappedVault } from "../nativeVault";
import { consumeLockedElsewhere, otherInstanceActive } from "../instanceLock";

const FAILS_KEY = "veto.lock.fails";
const FREE_ATTEMPTS = 5;

type FailState = { count: number; until: number };

function loadFails(): FailState {
  try {
    const raw = JSON.parse(localStorage.getItem(FAILS_KEY) ?? "{}") as Partial<FailState>;
    return { count: Number(raw.count) || 0, until: Number(raw.until) || 0 };
  } catch {
    return { count: 0, until: 0 };
  }
}

function saveFails(s: FailState): void {
  try {
    localStorage.setItem(FAILS_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

/** After 5 misses: 30 s, 1 min, 2 min … capped at 1 h (persisted across reloads). */
export function lockoutMs(failures: number): number {
  if (failures < FREE_ATTEMPTS) return 0;
  return Math.min(60 * 60_000, 30_000 * 2 ** (failures - FREE_ATTEMPTS));
}

function failClosedMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  if (/incorrect pin|wrong pin|invalid pin|unlock failed|decrypt|argon|vault/i.test(raw)) {
    return "Wrong passcode — your chats stay encrypted.";
  }
  return raw.trim() || "Unlock failed — your chats stay encrypted.";
}

export function LockScreen({ onUnlock }: { onUnlock: (pin: string) => Promise<void> }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [fails, setFails] = useState<FailState>(() => loadFails());
  const [now, setNow] = useState(() => Date.now());
  const [forgot, setForgot] = useState(false);
  const [confirmWord, setConfirmWord] = useState("");
  const [movedAway] = useState(() => consumeLockedElsewhere());
  const [openElsewhere, setOpenElsewhere] = useState(false);
  const waitMs = Math.max(0, fails.until - now);

  useEffect(() => {
    let alive = true;
    const check = () => void otherInstanceActive().then((v) => alive && setOpenElsewhere(v));
    check();
    const t = window.setInterval(check, 4000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (waitMs <= 0) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [waitMs]);

  async function submit() {
    if (busy || waitMs > 0 || pin.length < 6) return;
    setBusy(true);
    setError(null);
    try {
      await onUnlock(pin);
      saveFails({ count: 0, until: 0 });
    } catch (e) {
      const count = fails.count + 1;
      const next = { count, until: Date.now() + lockoutMs(count) };
      saveFails(next);
      setFails(next);
      setNow(Date.now());
      setError(failClosedMessage(e));
      setPin("");
      setShake(true);
      window.setTimeout(() => setShake(false), 420);
    } finally {
      setBusy(false);
    }
  }

  async function eraseAndStartOver() {
    if (confirmWord.trim().toUpperCase() !== "ERASE") return;
    setBusy(true);
    try {
      await wipeAllLocalData();
      await clearWrappedVault();
      for (const key of Object.keys(localStorage)) if (key.startsWith("veto.")) localStorage.removeItem(key);
      for (const key of Object.keys(sessionStorage)) if (key.startsWith("veto.")) sessionStorage.removeItem(key);
    } finally {
      window.location.replace("/app");
    }
  }

  if (forgot) {
    return (
      <div className="onboarding">
        <div className="card stack">
          <h2>Forgot your passcode?</h2>
          <p>
            There&apos;s no way to recover it — only you ever had it, and that&apos;s what keeps your chats
            private. Nobody, including Veto, can unlock this device&apos;s data without it.
          </p>
          <p className="muted">
            You can erase Veto on this device and start over with a new identity. Your messages here will be
            deleted and your contacts will need your new invite link.
          </p>
          <div className="field">
            <label htmlFor="eraseConfirm">Type ERASE to confirm</label>
            <input id="eraseConfirm" value={confirmWord} onChange={(e) => setConfirmWord(e.target.value)} autoComplete="off" />
          </div>
          <button
            className="btn danger"
            type="button"
            disabled={busy || confirmWord.trim().toUpperCase() !== "ERASE"}
            onClick={() => void eraseAndStartOver()}
          >
            Erase this device & start over
          </button>
          <button className="btn secondary" type="button" onClick={() => setForgot(false)} disabled={busy}>
            Back — I remember it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding">
      <form
        className={`card stack lock-card${shake ? " lock-shake" : ""}`}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="lock-orb" aria-hidden>
          <IconShield size={26} />
        </div>
        <div className="brand-lockup">
          <div className="brand-mark">V</div>
          <h1>Veto is locked</h1>
        </div>
        <p>Enter your passcode to open your chats.</p>
        {(movedAway || openElsewhere) && (
          <p className="muted text-sm lock-elsewhere" role="status">
            {movedAway
              ? "Veto was opened in another window, so it was locked here. Unlock to move it back."
              : "Veto is open in another window. Unlocking here moves it to this window."}
          </p>
        )}
        <div className="field">
          <label htmlFor="lockPin">Passcode</label>
          {/* Hidden username context so password managers treat this as a saved vault key. */}
          <input type="text" name="username" autoComplete="username" hidden aria-hidden="true" tabIndex={-1} />
          <input
            id="lockPin"
            type="password"
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              if (error) setError(null);
            }}
            autoComplete="current-password"
            autoFocus
            disabled={waitMs > 0}
            aria-invalid={!!error}
            aria-describedby={error ? "lockPinError" : undefined}
          />
        </div>
        {error && (
          <div id="lockPinError" className="text-danger lock-error" role="alert" aria-live="assertive">
            {error}
          </div>
        )}
        {waitMs > 0 && (
          <p className="muted text-sm" role="status">
            Too many wrong attempts — try again in {Math.ceil(waitMs / 1000)} s.
          </p>
        )}
        <button className="btn" disabled={busy || pin.length < 6 || waitMs > 0} type="submit">
          {busy ? "Unlocking…" : "Unlock"}
        </button>
        <button className="btn ghost small" type="button" onClick={() => setForgot(true)}>
          Forgot passcode?
        </button>
      </form>
    </div>
  );
}
